import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  BsCameraVideo,
  BsCameraVideoOff,
  BsMic,
  BsMicMute,
  BsFullscreen,
  BsTelephoneX,
  BsTelephoneOutboundFill,
} from 'react-icons/bs';
import { consultationApi } from '../api/consultationApi';
import { useRealtime } from '../context/RealtimeContext';
import '../styles/videoCall.css';
import '../styles/consultation.css';

function socketUrl() {
  if (process.env.REACT_APP_SOCKET_URL) return process.env.REACT_APP_SOCKET_URL;
  // In CRA dev (frontend on :3000) connect straight to the backend (:5000);
  // the dev-server websocket proxy is unreliable for socket.io.
  const { protocol, hostname, port } = window.location;
  if (port === '3000') return `${protocol}//${hostname}:5000`;
  return window.location.origin;
}

function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

const VideoConsultationRoom = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAnswering = searchParams.get('answer') === '1';
  const { incomingCall, clearIncoming } = useRealtime();
  const [error, setError] = useState('');
  const [joinInfo, setJoinInfo] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [peerName, setPeerName] = useState('');
  const [peerPresent, setPeerPresent] = useState(false);
  const [ringing, setRinging] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const stageRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const streamRef = useRef(null);
  const startedRef = useRef(false);
  const answerModeRef = useRef(isAnswering);
  const pendingCandidates = useRef([]);
  const timerRef = useRef(null);

  // Is the peer trying to (re)call us for THIS booking right now?
  const incomingForThis = incomingCall && String(incomingCall.bookingId) === String(bookingId);

  // Tear down the live media/socket/peer connection WITHOUT ending the
  // consultation booking — so the slot stays open for a recall.
  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.close();
      pcRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
  }, []);

  const attachSignalHandlers = (socket, pc) => {
    socket.on('signal', async ({ data }) => {
      try {
        if (data.sdp) {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          for (const candidate of pendingCandidates.current) {
            await pc.addIceCandidate(new RTCIceCandidate(candidate));
          }
          pendingCandidates.current = [];
          if (data.sdp.type === 'offer') {
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            socket.emit('signal', { bookingId, data: { sdp: pc.localDescription } });
          }
        } else if (data.candidate) {
          if (pc.remoteDescription) {
            await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
          } else {
            pendingCandidates.current.push(data.candidate);
          }
        }
      } catch (err) {
        console.error('Signal error', err);
      }
    });
  };

  const createOffer = async (socket, pc) => {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    socket.emit('signal', { bookingId, data: { sdp: pc.localDescription } });
  };

  // Establishes (or re-establishes) the whole call session. `answer` decides
  // whether we answer an incoming ring or place/ring the call ourselves.
  const startSession = useCallback(async ({ answer }) => {
    if (startedRef.current) return;
    startedRef.current = true;
    let cancelled = false;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const joinRes = await consultationApi.joinBooking(bookingId);
      setJoinInfo(joinRes.data);
      setPeerName(joinRes.data.counterpart?.name || 'Participant');

      // Call layer: answer an incoming call, otherwise ring the other party.
      // Both endpoints are idempotent server-side.
      try {
        if (answer) {
          await consultationApi.answerCall(bookingId);
        } else {
          const res = await consultationApi.startCall(bookingId);
          if (res.data?.call?.status === 'ringing') setRinging(true);
        }
      } catch (_) { /* non-fatal: the room still works without the ring layer */ }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;

      const pc = new RTCPeerConnection({ iceServers: joinRes.data.iceServers || [] });
      pcRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
        setPeerPresent(true);
      };
      pc.onconnectionstatechange = () => setConnectionState(pc.connectionState || 'connecting');
      pc.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          socketRef.current.emit('signal', { bookingId, data: { candidate: event.candidate } });
        }
      };

      const socket = io(`${socketUrl()}/consultation`, {
        auth: { token },
        transports: ['websocket', 'polling'],
      });
      socketRef.current = socket;
      attachSignalHandlers(socket, pc);

      socket.on('peer-joined', ({ name }) => {
        setPeerName(name || 'Participant');
        setPeerPresent(true);
        setRinging(false);
      });
      socket.on('peer-left', () => {
        setPeerPresent(false);
        setConnectionState('disconnected');
      });
      socket.on('call-ended', () => {
        // The other participant hung up. Keep us on the page with a recall
        // option instead of dropping straight back to the profile.
        cleanup();
        startedRef.current = false;
        setRinging(false);
        setPeerPresent(false);
        setCallEnded(true);
      });
      socket.on('connect_error', () => setConnectionState('failed'));

      socket.emit('join-room', { bookingId }, async (ack) => {
        if (!ack?.ok) {
          setError(ack?.message || 'Unable to join this consultation');
          return;
        }
        if (ack.peers && ack.peers.length > 0) {
          setPeerName(ack.peers[0].name || 'Participant');
          setRinging(false);
          await createOffer(socket, pc);
        }
      });

      timerRef.current = setInterval(() => setElapsed((v) => v + 1), 1000);
    } catch (err) {
      if (cancelled) return;
      startedRef.current = false;
      setError(err.response?.data?.msg || err.message || 'Unable to join this video consultation');
    }
  }, [bookingId, cleanup, navigate]);

  useEffect(() => {
    startSession({ answer: answerModeRef.current });
    return () => {
      startedRef.current = false;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, sessionKey]);

  // (Re)establish the call within the same slot. `answer` = respond to an
  // incoming recall; otherwise we place/ring the call.
  const restart = useCallback((answer) => {
    cleanup();
    startedRef.current = false;
    pendingCandidates.current = [];
    answerModeRef.current = answer;
    setCameraOn(true);
    setMicOn(true);
    setElapsed(0);
    setPeerPresent(false);
    setConnectionState('connecting');
    setRinging(false);
    setCallEnded(false);
    setSessionKey((k) => k + 1);
  }, [cleanup]);

  const toggleCamera = () => {
    const track = streamRef.current?.getVideoTracks()?.[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setCameraOn(track.enabled);
    socketRef.current?.emit('media-state', { bookingId, cameraOn: track.enabled, micOn });
  };

  const toggleMic = () => {
    const track = streamRef.current?.getAudioTracks()?.[0];
    if (!track) return;
    track.enabled = !track.enabled;
    setMicOn(track.enabled);
    socketRef.current?.emit('media-state', { bookingId, cameraOn, micOn: track.enabled });
  };

  // Hang up: end the current call but DON'T complete the booking, so it can be
  // recalled within the slot. Show the end screen with a recall option.
  const endCall = async () => {
    socketRef.current?.emit('end-call', { bookingId });
    try { await consultationApi.cancelCall(bookingId); } catch (_) { /* ignore */ }
    cleanup();
    startedRef.current = false;
    setRinging(false);
    setPeerPresent(false);
    setCallEnded(true);
  };

  // Recall (place a fresh call to the other party) within the slot.
  const recall = () => restart(false);

  // Answer a recall the other party just placed while we're on this page.
  const answerRecall = () => {
    if (incomingForThis) clearIncoming();
    restart(true);
  };

  const declineRecall = async () => {
    if (incomingForThis) clearIncoming();
    try { await consultationApi.declineCall(bookingId, 'Declined'); } catch (_) { /* ignore */ }
  };

  const leave = () => {
    cleanup();
    navigate('/profile');
  };

  const toggleFullscreen = async () => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    } else {
      await el.requestFullscreen();
    }
  };

  if (error) {
    return (
      <div className="vc-page">
        <div className="vc-error">
          <h2>Unable to join</h2>
          <p>{error}</p>
          <button className="consult-btn primary" type="button" onClick={() => navigate('/profile')}>
            Back to profile
          </button>
        </div>
      </div>
    );
  }

  const me = joinInfo?.identity;
  const other = joinInfo?.counterpart;
  const myLabel = me?.role === 'advocate' ? `Adv. ${me.name}` : me?.name;
  const otherLabel = other?.role === 'advocate' ? `Adv. ${other.name}` : other?.name;

  // Recall is allowed as long as we're still inside the booking's join window.
  const windowEnd = joinInfo?.booking?.joinWindowEnd
    ? new Date(joinInfo.booking.joinWindowEnd).getTime()
    : null;
  const withinSlot = windowEnd ? Date.now() <= windowEnd : true;

  if (callEnded) {
    // The peer is calling us back — offer Answer / Decline right here.
    if (incomingForThis) {
      return (
        <div className="vc-page">
          <div className="vc-ended">
            <div className="vc-ended-icon ring"><BsTelephoneOutboundFill /></div>
            <h2>Incoming call</h2>
            <p className="notranslate" translate="no">
              {incomingCall.callerName || otherLabel || 'The other participant'} is calling again…
            </p>
            <div className="vc-ended-actions">
              <button type="button" className="consult-btn primary" onClick={answerRecall}>
                Answer
              </button>
              <button type="button" className="consult-btn secondary" onClick={declineRecall}>
                Decline
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="vc-page">
        <div className="vc-ended">
          <div className="vc-ended-icon"><BsTelephoneX /></div>
          <h2>Call ended</h2>
          {withinSlot ? (
            <p>Your consultation slot is still open. You can call again if you got disconnected.</p>
          ) : (
            <p>The consultation window has now closed.</p>
          )}
          <div className="vc-ended-actions">
            {withinSlot && (
              <button type="button" className="consult-btn primary" onClick={recall}>
                <BsTelephoneOutboundFill style={{ marginRight: 8 }} />
                Call again
              </button>
            )}
            <button type="button" className="consult-btn secondary" onClick={leave}>
              Leave
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vc-page">
      <header className="vc-header">
        <div className="vc-identities">
          <strong>{otherLabel || 'Video Consultation'}</strong>
          <span>You: {myLabel || 'Connecting…'}</span>
        </div>
        <div className={`vc-status ${connectionState}`}>
          {connectionState === 'connected' ? 'Connected' : connectionState.replace('-', ' ')}
          {peerPresent ? ' · Participant in room' : ' · Waiting for the other participant'}
        </div>
        <div className="vc-timer">{formatElapsed(elapsed)}</div>
      </header>

      <div className="vc-stage" ref={stageRef}>
        <video ref={remoteVideoRef} className="vc-remote" autoPlay playsInline />
        {!peerPresent && (
          <div className="vc-placeholder">
            <div>
              {ringing
                ? `Ringing ${otherLabel || 'the other participant'}…`
                : `Waiting for ${otherLabel || 'the other participant'} to join…`}
            </div>
          </div>
        )}
        <div className="vc-name-tag">{otherLabel || 'Participant'}</div>
        <video ref={localVideoRef} className="vc-local" autoPlay playsInline muted />
      </div>

      <div className="vc-controls">
        <button type="button" className={`vc-ctrl ${cameraOn ? '' : 'off'}`} onClick={toggleCamera} title="Camera">
          {cameraOn ? <BsCameraVideo /> : <BsCameraVideoOff />}
        </button>
        <button type="button" className={`vc-ctrl ${micOn ? '' : 'off'}`} onClick={toggleMic} title="Microphone">
          {micOn ? <BsMic /> : <BsMicMute />}
        </button>
        <button type="button" className="vc-ctrl" onClick={toggleFullscreen} title="Full screen">
          <BsFullscreen />
        </button>
        <button type="button" className="vc-ctrl end" onClick={endCall} title="End call">
          <BsTelephoneX />
        </button>
      </div>
    </div>
  );
};

export default VideoConsultationRoom;
